import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { store } from 'store';
import {
    getLocalStorageValue,
    getMessageResponse,
    redirecLogin,
    setLocalStorageValue,
} from 'utils/helpers';
import { apiInstance } from './axiosInstance';
import { HTTP_CODE } from 'utils/constants';

const handleMessageResponse = (data: any, key: string) => {
    const messageData = data?.[key];
    if (!messageData) return null;
    return getMessageResponse(messageData);
};

const refreshToken = async (error: AxiosError): Promise<AxiosResponse | undefined> => {
    try {
        const originalRequest = error.config;
        const localAuthParams = getLocalStorageValue('authParams');
        const refreshToken = localAuthParams?.refreshToken;

        if (!refreshToken) {
            throw new Error('No refresh token');
        }

        const res = await apiInstance.post('/auth/refresh', undefined, {
            headers: {
                'Refresh-Token': refreshToken,
            },
        });

        const newAccessToken = res.data;

        setLocalStorageValue(
            'authParams',
            JSON.stringify({
                ...localAuthParams,
                accessToken: newAccessToken,
            }),
        );

        if (originalRequest) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiInstance(originalRequest);
        }
    } catch (e) {
        store.dispatch(redirecLogin());
        return Promise.reject(e);
    }
};

export const addBaseInterceptors = (instance: AxiosInstance) => {
    instance.interceptors.request.use((config) => {
        const localAuthParams = getLocalStorageValue('authParams');
        const accessToken = localAuthParams?.accessToken ?? '';

        if (accessToken) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    });

    instance.interceptors.response.use(
        (response: AxiosResponse) => {
            const messageRes = handleMessageResponse(response.data, 'message');
            if (messageRes) {
                return { ...response, data: { ...response.data, message: messageRes } };
            }
            return response;
        },
        async (error: AxiosError) => {
            const status = error.response?.status;
            const url = error.response?.config?.url ?? '';

            if (
                status === HTTP_CODE.UNAUTHORIZED &&
                !url.includes('auth/refresh') &&
                !url.includes('signin')
            ) {
                return refreshToken(error);
            }

            let messageRes;
            const isBlobError = error?.request?.responseType === 'blob';
            const responseData = error?.response?.data;

            if (isBlobError && responseData instanceof Blob) {
                try {
                    const text = await responseData.text();
                    const json = JSON.parse(text);
                    messageRes = handleMessageResponse(json, 'error');
                } catch (parseError) {
                    messageRes = handleMessageResponse(
                        { message_id: 'MSE00019', message_param: [] },
                        'error',
                    );
                }
            } else {
                messageRes = handleMessageResponse(responseData, 'error');
            }

            if (messageRes && error.response) {
                error.response = {
                    ...error.response,
                    data: {
                        ...(typeof error.response.data === 'object' && error.response.data !== null
                            ? error.response.data
                            : {}),
                        error: messageRes,
                    },
                    status: error.response.status ?? 500,
                    statusText: error.response.statusText ?? 'Error',
                    headers: error.response.headers ?? {},
                    config: error.response.config,
                    request: error.response.request,
                } as AxiosResponse;
            }

            return Promise.reject(error);
        },
    );

    return instance;
};
